import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { customersApi } from '../../api/customers.api';
import { APP_LOCALE } from '../../utils/locale.js';

/**
 * What has been done to this customer, by whom, and why.
 *
 * Every privileged action asks the operator for a reason. Until this existed
 * the only place that reason went was a log line, so the question a compliance
 * review actually asks — who froze this account in March, and what did they say
 * — had no answer here. Failed attempts are shown too: an account opening that
 * core banking refused is exactly what someone reconstructing an incident is
 * looking for, and it is the case a log was least likely to keep.
 */

const ACTION_LABELS: Record<string, string> = {
  'account.opened': 'Account opened',
  'account.freeze': 'Account frozen',
  'account.suspend': 'Account suspended',
  'account.close': 'Account closed',
  'account.unfreeze': 'Account unfrozen',
  'card.freeze': 'Card frozen',
  'card.cancel': 'Card cancelled',
  'transaction.accounts_viewed': 'Account numbers viewed',
  'customer.impersonation.started': 'Viewed as customer',
  'customer.pii.revealed': 'Personal data revealed',
  'comment.added': 'Comment added',
  'kyc.case.approved': 'KYC approved',
  'kyc.case.approve': 'KYC approval',
  'kyc.edd.keep': 'Keep EDD restriction',
  'kyc.case.rejected': 'KYC rejected',
  'customer.risk_rating.set': 'Risk rating set',
};

/** "customer.email_changed" → "Customer email changed", for actions without a label. */
function actionLabel(action: string): string {
  const known = ACTION_LABELS[action];
  if (known) return known;
  const words = action.replace(/^admin\./, '').replace(/[._]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Wording stored by older releases, said shorter. The stored text is unchanged. */
const SHORT_REASONS: Record<string, string> = {
  'Full transaction accounts displayed in the operational Transactions workspace':
    'Opened Transactions with full account numbers',
};

type ActionEntry =Awaited<ReturnType<typeof customersApi.actions>>[number];

/** Consecutive identical entries, shown as one line with a count. */
interface ActivityRow {
  readonly entry: ActionEntry;
  readonly count: number;
  readonly earliest: string;
}

/**
 * Folds runs of the same action, by the same person, for the same reason.
 *
 * Opening the Transactions page once used to write one "accounts viewed"
 * record per row (fixed in the API); those rows are still in the history, and
 * forty identical lines hide the freeze that someone is actually looking for.
 */
export function foldRepeats(entries: readonly ActionEntry[]): ActivityRow[] {
  const rows: ActivityRow[] = [];
  for (const entry of entries) {
    const last = rows[rows.length - 1];
    const same =
      last !== undefined &&
      last.entry.action === entry.action &&
      last.entry.adminId === entry.adminId &&
      last.entry.reason === entry.reason &&
      last.entry.outcome === entry.outcome;
    if (same) {
      rows[rows.length - 1] = { ...last, count: last.count + 1, earliest: entry.createdAt };
    } else {
      rows.push({ entry, count: 1, earliest: entry.createdAt });
    }
  }
  return rows;
}

function whoDidIt(entry: ActionEntry): string {
  if (entry.adminEmail) return entry.adminEmail;
  if (entry.adminId.startsWith('system')) return 'System';
  return 'Unknown staff member';
}

interface CustomerActivityTabProps {
  customerId: string;
}

export function CustomerActivityTab({ customerId }: CustomerActivityTabProps): JSX.Element {
  const actionsQuery = useQuery({
    queryKey: ['customer-actions', customerId],
    queryFn: () => customersApi.actions(customerId),
  });

  if (actionsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (actionsQuery.isError) {
    return <Alert severity="error">Could not load this customer’s action history.</Alert>;
  }

  const actions = actionsQuery.data ?? [];

  if (actions.length === 0) {
    return (
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          No privileged actions have been taken against this customer. Opening, freezing,
          suspending or closing an account, and freezing or cancelling a card, are all recorded
          here with the reason the operator gave.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="caption" color="text.secondary">
        Every privileged action and the reason given for it, newest first. Attempts that failed
        are kept as well as ones that worked.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Outcome</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {foldRepeats(actions).map(({ entry, count, earliest }) => (
            <TableRow key={entry.id}>
              <TableCell>
                {new Date(entry.createdAt).toLocaleString(APP_LOCALE)}
                {count > 1 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    since {new Date(earliest).toLocaleString(APP_LOCALE)}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">
                    {actionLabel(entry.action)}
                  </Typography>
                  {count > 1 ? <Chip size="small" label={`×${String(count)}`} /> : null}
                  {entry.subjectId !== null && count === 1 ? (
                    <Tooltip title={`${entry.subjectType ?? ''} ${entry.subjectId}`}>
                      <Chip size="small" variant="outlined" label={entry.subjectId.slice(0, 8)} />
                    </Tooltip>
                  ) : null}
                </Stack>
              </TableCell>
              <TableCell>
                {entry.outcome === 'SUCCEEDED' ? (
                  <Chip size="small" color="success" variant="outlined" label="Succeeded" />
                ) : (
                  <Tooltip
                    title={
                      typeof entry.detail === 'object' &&
                      entry.detail !== null &&
                      'error' in entry.detail
                        ? String((entry.detail as { error: unknown }).error)
                        : 'The attempt did not complete'
                    }
                  >
                    <Chip size="small" color="error" variant="outlined" label="Failed" />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>{SHORT_REASONS[entry.reason] ?? entry.reason}</TableCell>
              <TableCell>
                <Tooltip title={entry.adminId}>
                  <Typography variant="body2">{whoDidIt(entry)}</Typography>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}
